import { v4 as uuidv4 } from "uuid";

import { config } from "../config";
import {
  TSession,
  TUserInfo,
  TProcedure,
  TView,
  TTransformedItem,
  TSimulationRow,
  TUpload,
  TRole,
  TPlan,
  TSurgeryStageType,
  PreSignedUrlPair,
} from "../utils/types";
import { procedureIconMap } from "../utils/constant";
import { fetchWithAuthRetry } from "../utils/fetchWithAuthRetry";
import { waitForJobMetadata } from "./jobMetadataPolling";
import {
  uploadImageWithDiagnostics,
  ImageUploadDiagnostics,
  SIMULATION_TIMEOUT_MS,
  SimulationPipelineError,
  TBackendFailureType,
  TSimulationStage,
} from "./simulationPipeline";

const protocol = config.coreApi.protocol;
const host = config.coreApi.host;
const urlPrefix = `${protocol}://${host}/clinics/v1/clinic`;
const urlPrefixUser = `${protocol}://${host}/clinics/v1/user`;
const urlPrefixMgmtClinic = `${protocol}://${host}/clinics/v1/mgmt-clinic/clinics`;
const urlPrefixAppointment = `${protocol}://${host}/clinics/v1/appointment`;

const createHttpResponseError = async (
  response: Response,
  context?: string,
): Promise<Error> => {
  let responseBody = "";

  try {
    responseBody = await response.text();
  } catch (error) {
    console.warn("Unable to read failed HTTP response body", error);
  }

  const contextPrefix = context ? `${context}: ` : "";
  const bodySuffix = responseBody ? ` - ${responseBody}` : "";

  return new Error(
    `${contextPrefix}HTTP Error: ${response.status}${bodySuffix}`,
  );
};

export type TReturnGetImagesBatchBatchId = {
  procedureType: TProcedure[];
  viewType: TView | null;
  presignedUrls: string[];
  uploadType: TUpload;
};

export const getMgmtImagesBatchBatchId = async (
  shareId: string,
  pin: string,
): Promise<TReturnGetImagesBatchBatchId[]> => {
  const headers = {
    "Content-Type": "application/json",
  };
  try {
    const response = await fetch(
      `${protocol}://${host}/clinics/v1/shared/${shareId}/batch/images?pin=${pin}`,
      {
        method: "GET",
        headers,
      },
    );

    if (!response.ok) {
      throw new Error(`HTTP error ${response.status}`);
    }

    const data = await response.json();
    const result = data.images.map((item: any) => {
      const procedureTypeViewTypePresignedUrls: TReturnGetImagesBatchBatchId = {
        procedureType: item.image_data.procedure_types,
        viewType: item.image_data.view_type,
        uploadType: item.image_data.upload_type,
        presignedUrls: item.presigned_urls,
      };
      return procedureTypeViewTypePresignedUrls;
    });
    return result;
  } catch (error) {
    throw error;
  }
};

export const createAClinic = async (
  sessionAccessToken: string,
  display_name: string,
  primary_goal?: string,
) => {
  try {
    const uuid = uuidv4();
    const response = await fetchWithAuthRetry(
      `${protocol}://${host}/clinics/v1/mgmt-clinic/clinics`,
      {
        method: "POST",
        body: JSON.stringify({
          display_name: display_name,
          sdk_keys: [uuid],
          ...(primary_goal ? { primary_goal } : {}),
        }),
      },
    );

    if (!response.ok) {
      const text = await response.text();
      throw new Error(`HTTP Error: ${response.status} - ${text}`);
    }

    const j = await response.json();
    return j;
  } catch (error) {
    console.error("Error creating clinic:", error);
    throw error;
  }
};

export const addUserToClinic = async (
  sessionAccessToken: string,
  clinicId: string,
  userId: string,
  role: TRole,
) => {
  try {
    const response = await fetchWithAuthRetry(
      `${protocol}://${host}/clinics/v1/mgmt-clinic/clinics/${clinicId}/users`,
      {
        method: "POST",
        body: JSON.stringify({
          user_uuid: userId,
          role: role,
        }),
      },
    );

    if (!response.ok) {
      const text = await response.text();
      throw new Error(`HTTP Error: ${response.status} - ${text}`);
    }

    const j = await response.json();
    return j;
  } catch (error) {
    console.error("Error in batchTransform:", error);
    throw error;
  }
};

export const getUserInfo = async (
  accessToken: string,
  userId: string,
  options: { signal?: AbortSignal } = {},
): Promise<TUserInfo | null> => {
  const headers = {
    Authorization: `Bearer ${accessToken}`,
    "Content-Type": "application/json",
  };

  try {
    const response = await fetch(
      `${protocol}://${host}/clinics/v1/user/${userId}`,
      {
        method: "GET",
        headers,
        signal: options.signal,
      },
    );

    if (!response.ok) {
      throw new Error(`HTTP error ${response.status}`);
    }

    const data = await response.json();
    const userInfo: TUserInfo = {
      role: data.user_info[0].role,
      sdkKeys: data.user_info[0].sdk_keys,
      planType: data.user_info[0].plan_type,
      clinicId: data.user_info[0].clinic_id,
      clinicName: data.user_info[0].display_name,
      displayName: data.user_info[0].display_name,
      completedSetup: data.user_info[0].completed_setup,
      showOnboardTipsBeforeAfter:
        data.user_info[0].show_onboard_tips_before_after,
    };

    return userInfo;
  } catch (error: any) {
    if (error?.name === "AbortError") {
      throw error;
    }
    console.error("Error getting user info:", error);
    const userInfo: TUserInfo = {
      role: "",
      sdkKeys: [],
      planType: "",
      clinicId: "",
      clinicName: "",
      displayName: "",
      completedSetup: false,
      showOnboardTipsBeforeAfter: true,
    };
    return userInfo;
  }
};

export const getSupportedViewTypeAndProcedureType = async (
  session: TSession,
  viewTypes: TView[],
): Promise<
  {
    viewType: TView;
    procedureType: TProcedure;
  }[]
> => {
  try {
    const queryParams = new URLSearchParams();
    viewTypes.forEach((viewType) => {
      queryParams.append("view_types", viewType);
    });
    const queryString = queryParams.toString();

    const response = await fetchWithAuthRetry(
      `${protocol}://${host}/clinics/v1/mgmt-clinic/info/supported-procedures-for-view?${queryString}`,
      {
        method: "GET",
      },
    );

    if (!response.ok) {
      const text = await response.text();
      throw new Error(`HTTP Error: ${response.status} - ${text}`);
    }

    const j = await response.json();
    const result = j.map((item: { procedure_type: any; view_type: string }) => {
      return {
        procedureType: item.procedure_type,
        viewType: item.view_type,
      };
    });
    return result;
  } catch (error) {
    console.error("Error in getting supported procedures for view:", error);
    throw error;
  }
};

export const getSupportedProceduresForView = async (
  session: TSession,
  data: { presignedBeforeUrl: string; viewType: TView }[],
  options: { signal?: AbortSignal } = {},
): Promise<
  {
    image_url: string;
    procedure_view: TView;
    procedure_types: TProcedure[];
  }[]
> => {
  try {
    const viewTypes = data.map((item) => item.viewType);
    const queryParams = new URLSearchParams();
    viewTypes.forEach((viewType) => {
      queryParams.append("view_types", viewType);
    });
    const queryString = queryParams.toString();

    const response = await fetchWithAuthRetry(
      `${protocol}://${host}/clinics/v1/mgmt-clinic/info/supported-procedures-for-view?${queryString}`,
      {
        method: "GET",
        signal: options.signal,
      },
    );

    if (!response.ok) {
      const text = await response.text();
      throw new Error(`HTTP Error: ${response.status} - ${text}`);
    }

    const j = await response.json();

    const result = j.map((obj1: { procedure_type: any; view_type: string }) => {
      const obj2 = data.find(
        (obj: { viewType: string }) => obj.viewType === obj1.view_type,
      );
      return {
        procedure_types: [obj1.procedure_type],
        procedure_view: obj1.view_type,
        image_url: obj2?.presignedBeforeUrl,
      };
    });

    result.sort(
      (a: { procedure_view: TView }, b: { procedure_view: TView }) => {
        const viewTypeOrder = {
          TORSO_FRONT: 1,
          TORSO_LEFT_45: 2,
          TORSO_LEFT: 3,
          TORSO_RIGHT_45: 4,
          TORSO_RIGHT: 5,
          BODY_FRONT: 7,
          BODY_BACK: 8,
          BODY_LEFT: 9,
          BODY_RIGHT: 10,
          HEAD_FRONT: 11,
          HEAD_LEFT_45: 12,
          HEAD_LEFT: 13,
          HEAD_RIGHT_45: 14,
          HEAD_RIGHT: 15,
        };

        // @ts-ignore
        const viewTypeA = viewTypeOrder[a.procedure_view] || Infinity;
        // @ts-ignore
        const viewTypeB = viewTypeOrder[b.procedure_view] || Infinity;

        return viewTypeA - viewTypeB;
      },
    );

    return result;
  } catch (error) {
    console.error("Error in batchTransform:", error);
    throw error;
  }
};

// Input type for the function
export type ImageUploadItem = {
  file: File;
  viewType: TView;
  procedureTypes: TProcedure[];
};

// Request schema to match backend
export type PatientImageForBatch = {
  view_type?: TView;
  procedure_types: TProcedure[];
  surgery_stage?: TSurgeryStageType;
  taken_in_clinic?: boolean;
  days_postop?: number;
  date_taken?: string; // ISO date string
  preop_image?: boolean;
  external_id?: string;
  tags?: string[];
};

export type CreatePatientImageBatchRequest = {
  images: PatientImageForBatch[];
  external_id?: string;
  tags?: string[];
};
export type CreatePatientImageBatchResponse = {
  presigned_urls: PreSignedUrlPair[];
  batch_id: number;
};

export type PostImagesBatchResult = {
  batchId: number;
  viewTypeAndPresignedBeforeUrls: {
    viewType: TView;
    presignedBeforeUrl: string;
  }[];
};

export const postImagesBatch = async (
  session: TSession,
  patientId: string,
  imageItems: ImageUploadItem[],
  surgeryStage: TSurgeryStageType,
  options?: {
    takenInClinic?: boolean;
    daysPostop?: number;
    dateTaken?: string;
    externalId?: string;
    tags?: string[];
    batchId?: number;
    signal?: AbortSignal;
    onStageChange?: (stage: TSimulationStage) => void;
  },
): Promise<PostImagesBatchResult> => {
  // Convert imageItems to the batch format
  const images: PatientImageForBatch[] = imageItems.map((item) => ({
    view_type: item.viewType,
    procedure_types: item.procedureTypes,
    surgery_stage: surgeryStage,
    taken_in_clinic: options?.takenInClinic,
    days_postop: options?.daysPostop,
    date_taken: options?.dateTaken,
    preop_image: ["preop", "markings"].includes(surgeryStage),
    external_id: options?.externalId,
    tags: options?.tags,
  }));

  const bodyContent: CreatePatientImageBatchRequest = {
    images,
    external_id: options?.externalId,
    tags: options?.tags,
  };

  try {
    const url = new URL(
      `${urlPrefix}/${session.clinicId}/patients/${patientId}/images/batch`,
    );
    url.searchParams.append("source", "dashboard");

    options?.onStageChange?.("upload_registration");
    const response = await fetchWithAuthRetry(url.toString(), {
      method: "POST",
      body: JSON.stringify(bodyContent),
      signal: options?.signal,
    });

    if (!response.ok) {
      throw await createHttpResponseError(
        response,
        "Failed to register image upload batch",
      );
    }

    const responseData: CreatePatientImageBatchResponse = await response.json();

    // Upload files to presigned URLs
    options?.onStageChange?.("image_upload");
    const presignedBeforeUrls = await Promise.all(
      responseData.presigned_urls.map(
        async (urlPair: PreSignedUrlPair, index: number) => {
          const uploadResponse = await fetch(urlPair.put_url, {
            method: "PUT",
            headers: {
              "Content-Type": imageItems[index].file.type || "image/png",
            },
            body: imageItems[index].file,
            signal: options?.signal,
          });

          if (!uploadResponse.ok) {
            throw await createHttpResponseError(
              uploadResponse,
              `Failed to upload image ${index + 1}`,
            );
          }

          return urlPair.get_url;
        },
      ),
    );

    // Map results back with view types
    const viewTypeAndPresignedBeforeUrls = presignedBeforeUrls.map(
      (getUrl, index) => ({
        viewType: imageItems[index].viewType,
        presignedBeforeUrl: getUrl,
      }),
    );

    return {
      batchId: responseData.batch_id,
      viewTypeAndPresignedBeforeUrls,
    };
  } catch (error) {
    console.error("Error uploading images:", error);
    throw error;
  }
};

type ProcedureConfig = {
  procedure_types: TProcedure[];
  procedure_view?: TView;
};

const V2_TRANSFORM_MODEL = "v2";

const V2_SUPPORTED_PROCEDURE_COMBINATIONS = new Set(
  [
    ["BBL"],
    ["BREAST_AUGMENTATION"],
    ["BREAST_LIFT"],
    ["BREAST_REDUCTION"],
    ["TUMMY_TUCK"],
    ["CHIN_LIPOSUCTION"],
    ["LIP_FILLER"],
    ["RHINOPLASTY"],
    ["HAIR_TRANSPLANT"],
  ].map((procedureTypes) => procedureTypes.sort().join("|")),
);

const getProcedureCombinationKey = (procedureTypes: TProcedure[]) =>
  [...procedureTypes].sort().join("|");

const shouldUseV2ModelForProcedureTypes = (procedureTypes: TProcedure[]) =>
  V2_SUPPORTED_PROCEDURE_COMBINATIONS.has(
    getProcedureCombinationKey(procedureTypes),
  );

const shouldUseV2ModelForBatchRequest = (
  request: {
    procedure_types: TProcedure[];
  }[],
) =>
  request.length > 0 &&
  request.every((item) =>
    shouldUseV2ModelForProcedureTypes(item.procedure_types),
  );

const getProcedureConfig = (procedure: TProcedure): ProcedureConfig => {
  const procedureMap: Record<TProcedure, ProcedureConfig> = {
    BBL: { procedure_types: ["BBL"] },
    BREAST_REDUCTION: { procedure_types: ["BREAST_REDUCTION"] },
    BREAST_AUGMENTATION: { procedure_types: ["BREAST_AUGMENTATION"] },
    TUMMY_TUCK: {
      procedure_types: ["TUMMY_TUCK"],
      procedure_view: "BODY_FRONT",
    },
    ABDOMEN_LIPOSUCTION: {
      procedure_types: ["ABDOMEN_LIPOSUCTION"],
      procedure_view: "BODY_FRONT",
    },
    LIP_FILLER: {
      procedure_types: ["LIP_FILLER"],
      procedure_view: "HEAD_FRONT",
    },
    BREAST_LIFT: {
      procedure_types: ["BREAST_LIFT"],
      procedure_view: "TORSO_FRONT",
    },
    BREAST_LIFT_AND_AUGMENTATION: {
      procedure_types: ["BREAST_LIFT_AND_AUGMENTATION"],
      procedure_view: "TORSO_FRONT",
    },
    TUMMY_TUCK_AND_BREAST_LIFT: {
      procedure_types: ["TUMMY_TUCK", "BREAST_LIFT"],
    },
    TUMMY_TUCK_AND_BREAST_REDUCTION: {
      procedure_types: ["TUMMY_TUCK", "BREAST_REDUCTION"],
    },
    TUMMY_TUCK_AND_BREAST_AUGMENTATION: {
      procedure_types: ["TUMMY_TUCK", "BREAST_AUGMENTATION"],
    },
    THIGH_LIPOSUCTION: { procedure_types: ["THIGH_LIPOSUCTION"] },
    CHIN_LIPOSUCTION: {
      procedure_types: ["CHIN_LIPOSUCTION"],
      procedure_view: "HEAD_LEFT",
    },
    HAIR_TRANSPLANT: {
      procedure_types: ["HAIR_TRANSPLANT"],
      procedure_view: "HEAD_FRONT",
    },
    ARMS_LIPOSUCTION: { procedure_types: ["ARMS_LIPOSUCTION"] },
    RHINOPLASTY: { procedure_types: ["RHINOPLASTY"] },
    BLEPHAROPLASTY: { procedure_types: ["BLEPHAROPLASTY"] },
    RHYTIDECTOMY: { procedure_types: ["RHYTIDECTOMY"] },
    OTOPLASTY: { procedure_types: ["OTOPLASTY"] },
    FOREHEAD_LIFT: { procedure_types: ["FOREHEAD_LIFT"] },
    CHIN_SURGERY: { procedure_types: ["CHIN_SURGERY"] },
    CHEEK_AUGMENTATION: { procedure_types: ["CHEEK_AUGMENTATION"] },
    LIPOSUCTION: { procedure_types: ["LIPOSUCTION"] },
    BODY_LIFT: { procedure_types: ["BODY_LIFT"] },
    BUTTOCK_AUGMENTATION: { procedure_types: ["BUTTOCK_AUGMENTATION"] },
    ARM_LIFT: { procedure_types: ["ARM_LIFT"] },
    THIGH_LIFT: { procedure_types: ["THIGH_LIFT"] },
    BURN_REPAIR: { procedure_types: ["BURN_REPAIR"] },
    SCAR_REVISION: { procedure_types: ["SCAR_REVISION"] },
    HAND_SURGERY: { procedure_types: ["HAND_SURGERY"] },
    RECONSTRUCTIVE_SURGERY: { procedure_types: ["RECONSTRUCTIVE_SURGERY"] },
    BOTOX_INJECTIONS: { procedure_types: ["BOTOX_INJECTIONS"] },
    DERMAL_FILLERS: { procedure_types: ["DERMAL_FILLERS"] },
    LASER_TREATMENTS: { procedure_types: ["LASER_TREATMENTS"] },
    MICRODERMABRASION: { procedure_types: ["MICRODERMABRASION"] },
    FACE_LIFT: { procedure_types: ["FACE_LIFT"] },
    LASER_HAIR_REMOVAL: { procedure_types: ["LASER_HAIR_REMOVAL"] },
  };

  const procedureConfig = procedureMap[procedure];
  if (!procedureConfig) {
    throw new Error(`Unknown procedure: ${procedure}`);
  }
  return procedureConfig;
};

export const getBeforeImageUrl = async (
  session: TSession,
  selectedImage: Blob,
  procedure: TProcedure,
  abortController: AbortController,
  onStageChange?: (stage: TSimulationStage) => void,
  reportUpload: (diagnostics: ImageUploadDiagnostics) => void = () => {},
) => {
  if (!session?.clinicId) {
    throw new Error("Missing clinicId in session");
  }

  const procedureConfig = getProcedureConfig(procedure);

  const createImageUrl = new URL(`${urlPrefix}/${session.clinicId}/images`);
  createImageUrl.searchParams.append("source", "dashboard");

  onStageChange?.("upload_registration");
  let response = await fetchWithAuthRetry(createImageUrl.toString(), {
    method: "POST",
    body: JSON.stringify({
      num_images: 1,
      mime_type: selectedImage.type || "image/png",
      procedures: procedureConfig.procedure_types,
      ...(procedureConfig.procedure_view && {
        view_type: procedureConfig.procedure_view,
      }),
    }),
    signal: abortController.signal,
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(`HTTP Error: ${response.status} - ${text}`);
  }

  const j = await response.json();
  const urlPair = j?.presigned_urls?.[0];
  if (!urlPair?.put_url || !urlPair?.get_url) {
    throw new Error("Invalid image upload response");
  }

  onStageChange?.("image_upload");
  await uploadImageWithDiagnostics(
    {
      putUrl: urlPair.put_url,
      image: selectedImage,
      signal: abortController.signal,
    },
    reportUpload,
  );

  return urlPair.get_url as string;
};

const checkUrlAvailabilityWithSignal = async (
  url: string,
  abortController: AbortController,
) =>
  waitForJobMetadata<string>({
    metadataUrl: url,
    signal: abortController.signal,
    intervalMs: 500,
    timeoutMs: SIMULATION_TIMEOUT_MS,
    statusLabel: "Simulation result",
    resolveResponse: async () => url,
  });

export const getAfterImageUrls = async (
  session: TSession,
  beforeImageUrl: string,
  procedure: TProcedure,
  abortController: AbortController,
  patientId?: string,
  onStageChange?: (stage: TSimulationStage) => void,
  attemptId?: string,
  reportUpload: (diagnostics: ImageUploadDiagnostics) => void = () => {},
) => {
  if (!session?.clinicId) {
    throw new Error("Missing clinicId in session");
  }

  const procedureConfig = getProcedureConfig(procedure);
  const endpoint = patientId
    ? `${urlPrefix}/${session.clinicId}/patients/${patientId}/transforms`
    : `${urlPrefix}/${session.clinicId}/transforms`;
  const url = new URL(endpoint);
  url.searchParams.append("source", "dashboard");
  if (shouldUseV2ModelForProcedureTypes(procedureConfig.procedure_types)) {
    url.searchParams.append("model", V2_TRANSFORM_MODEL);
  }

  onStageChange?.("transform_submission");
  const response = await fetchWithAuthRetry(url.toString(), {
    method: "POST",
    body: JSON.stringify({
      image_url: beforeImageUrl,
      procedure_types: procedureConfig.procedure_types,
      ...(procedureConfig.procedure_view && {
        procedure_view: procedureConfig.procedure_view,
      }),
    }),
    signal: abortController.signal,
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(`HTTP Error: ${response.status} - ${text}`);
  }

  const j = await response.json();

  const downloadMetaData = async (metadataUrl: string) => {
    if (!metadataUrl) {
      return null;
    }
    onStageChange?.("metadata_polling");
    return waitForJobMetadata<any>({
      metadataUrl,
      signal: abortController.signal,
      intervalMs: 500,
      timeoutMs: SIMULATION_TIMEOUT_MS,
      statusLabel: "Simulation",
    });
  };

  const metaData = j.metadata_url
    ? await downloadMetaData(j.metadata_url)
    : null;

  if (metaData && metaData.status.success === false) {
    const backendFailureType: TBackendFailureType | undefined =
      metaData.status.failure_type === "image_validation" ||
      metaData.status.failure_type === "worker_error"
        ? metaData.status.failure_type
        : undefined;
    throw new SimulationPipelineError({
      message:
        metaData.status.message ||
        metaData.status.reason ||
        "The simulation could not process this image.",
      code: "MODEL_REJECTED_IMAGE",
      backendCode: metaData.status.code,
      backendFailureType,
      backendRetryable:
        typeof metaData.status.retryable === "boolean"
          ? metaData.status.retryable
          : undefined,
      stage: "metadata_polling",
    });
  }

  onStageChange?.("result_polling");
  const imageUrls =
    j.download_urls && j.download_urls.length > 0
      ? await Promise.all(
          j.download_urls.map((downloadUrl: string) =>
            checkUrlAvailabilityWithSignal(downloadUrl, abortController),
          ),
        )
      : [];

  if (imageUrls.length === 0) {
    throw new SimulationPipelineError({
      message: "The simulation completed without any generated images.",
      code: "RESULT_POLL_FAILED",
      stage: "result_polling",
    });
  }

  return {
    imageUrls,
    metaData: {
      reason: metaData?.reason,
      success: metaData?.status.success,
      implant_size_in_cc: metaData?.implant_size_in_cc ?? null,
    },
    imageData: j.image_data ?? null,
  };
};

export const assignPatientToClinicImage = async (
  session: TSession,
  imageId: number,
  patientUuid: string,
): Promise<void> => {
  const response = await fetchWithAuthRetry(
    `${urlPrefix}/${session.clinicId}/images/${imageId}/assign-patient`,
    {
      method: "PATCH",
      body: JSON.stringify({ patient_uuid: patientUuid }),
    },
  );

  if (response.status === 204) return;

  const errorText = await response.text();
  throw new Error(
    `Failed to assign image ${imageId} to patient ${patientUuid}: HTTP ${response.status} - ${errorText}`,
  );
};

export const postTransformsBatch = async (
  session: TSession,
  patientId: string,
  batchId: string | number,
  imageUrlProcedureTypeProcedureView: {
    image_url: string;
    procedure_view: TView;
    procedure_types: TProcedure[];
  }[],
  options: { signal?: AbortSignal } = {},
): Promise<TTransformedItem[] | null> => {
  try {
    const body = {
      request: imageUrlProcedureTypeProcedureView,
      batch_id: batchId,
    };

    const url = new URL(
      `${urlPrefix}/${session.clinicId}/patients/${patientId}/transforms/batch`,
    );
    url.searchParams.append("source", "dashboard");
    if (shouldUseV2ModelForBatchRequest(imageUrlProcedureTypeProcedureView)) {
      url.searchParams.append("model", V2_TRANSFORM_MODEL);
    }
    const response = await fetchWithAuthRetry(url.toString(), {
      method: "POST",
      body: JSON.stringify(body),
      signal: options.signal,
    });

    if (!response.ok) {
      const text = await response.text();
      throw new Error(`HTTP Error: ${response.status} - ${text}`);
    }

    const j = await response.json();
    const transformedData = imageUrlProcedureTypeProcedureView.map(
      (item, index) => ({
        ...item,
        presigned_after_urls: j.response[index].presigned_urls,
        meta_data: j.response[index].metadata_url,
        downloaded_after_urls: [],
        downloaded_meta_data: {
          success: true,
          reason: null,
          implantSize: null,
        },
        procedure_icon: procedureIconMap[item.procedure_types[0]],
      }),
    );

    return transformedData;
  } catch (error) {
    console.error("Error in batchTransform:", error);
    throw error;
  }
};

export const pollForData = async (
  url: string,
  interval = 500,
  _maxRetries = 2000,
  options: { signal?: AbortSignal; timeoutMs?: number } = {},
) => {
  const data = await waitForJobMetadata<any>({
    metadataUrl: url,
    signal: options.signal,
    intervalMs: interval,
    timeoutMs: options.timeoutMs ?? SIMULATION_TIMEOUT_MS,
    statusLabel: "Simulation",
  });

  return {
    success: data.status.success,
    reason: data.status.reason,
    implantSize: data.status.implant_size_in_cc,
  };
};

export const checkUrlAvailability = async (
  url: string,
  options: { signal?: AbortSignal; timeoutMs?: number } = {},
) =>
  waitForJobMetadata<string>({
    metadataUrl: url,
    signal: options.signal,
    intervalMs: 4000,
    timeoutMs: options.timeoutMs ?? SIMULATION_TIMEOUT_MS,
    statusLabel: "Simulation result",
    resolveResponse: async () => url,
  });

export const getImagesBatch = async (
  session: TSession,
  patientId: string,
): Promise<TSimulationRow[]> => {
  try {
    const response = await fetchWithAuthRetry(
      `${urlPrefix}/${session.clinicId}/patients/${patientId}/images/batch`,
      {
        method: "GET",
      },
    );

    // A 404 here just means the patient has no image batches yet — not an error.
    if (response.status === 404) {
      return [];
    }

    if (!response.ok) {
      throw new Error(`HTTP error ${response.status}`);
    }

    const data = await response.json();

    const simulationRows = (data.batches ?? []).map(
      (item: {
        id: any;
        created_at: any;
        procedure_types: any;
        updated_at: any;
        upload_type: any;
      }) => {
        const simulationRow: TSimulationRow = {
          id: item.id,
          created_at: item.created_at,
          procedure_types: item.procedure_types,
          updated_at: item.updated_at,
          upload_type: item.upload_type,
        };
        return simulationRow;
      },
    );
    return simulationRows;
  } catch (error) {
    console.error("Error getting image batches:", error);
    throw error;
  }
};

export const postTransformBatchShare = async (
  session: TSession,
  patientId: string,
  batchId: string,
): Promise<{
  pin: string;
  shareId: string;
}> => {
  const body = {
    batch_id: batchId,
  };

  try {
    const response = await fetchWithAuthRetry(
      `${urlPrefix}/${session.clinicId}/patients/${patientId}/transforms/batch/share`,
      {
        method: "POST",
        body: JSON.stringify(body),
      },
    );

    if (!response.ok) {
      throw new Error(`HTTP error ${response.status}`);
    }

    const data = await response.json();
    const result = {
      pin: data.pin ?? "",
      shareId: data.share_id ?? "",
    };
    return result;
  } catch (error) {
    console.error("Error sharing transform batch:", error);
    throw error;
  }
};

export const getImagesBatchBatchId = async (
  session: TSession,
  patientId: string,
  batchId: string,
): Promise<TReturnGetImagesBatchBatchId[]> => {
  try {
    const response = await fetchWithAuthRetry(
      `${urlPrefix}/${session.clinicId}/patients/${patientId}/images/batch/${batchId}`,
      {
        method: "GET",
      },
    );

    if (!response.ok) {
      throw new Error(`HTTP error ${response.status}`);
    }

    const data = await response.json();

    const result = data.images.map((item: any) => {
      const procedureTypeViewTypePresignedUrls: TReturnGetImagesBatchBatchId = {
        procedureType: item.image_data.procedure_types,
        viewType: item.image_data.view_type,
        uploadType: item.image_data.upload_type,
        presignedUrls: item.presigned_urls,
      };
      return procedureTypeViewTypePresignedUrls;
    });

    return result;
  } catch (error) {
    console.error("Error getting batch images:", error);
    throw error;
  }
};

// Endpoint to get individual patient images (not in a batch)
export type TGetPatientImageData = {
  image_data: {
    id: number;
    procedure_types: string[];
    procedure_types_human_readable: string[];
    view_type: string | null;
    view_type_human_readable: string | null;
    upload_type: string;
    uploaded: boolean;
    days_postop: number | null;
    surgery_stage: string | null;
    taken_in_clinic: boolean | null;
    is_starred: boolean;
    external_id: string | null;
    tags: string[];
    created_at: string;
    updated_at: string;
    date_taken?: string | null;
  };
  presigned_urls: string[];
  thumbnail_presigned_urls: string[];
  edit_target?: {
    target_type: "clinic_patient_image";
    target_id: number;
  } | null;
};

export const getPatientImages = async (
  session: TSession,
  patientId: string,
  options?: {
    isStarred?: boolean;
    uploadTypes?: string[];
    isInBatch?: boolean;
  },
): Promise<TGetPatientImageData[]> => {
  const queryParams = new URLSearchParams();
  if (options?.isStarred !== undefined) {
    queryParams.append("is_starred", String(options.isStarred));
  }
  if (options?.uploadTypes) {
    options.uploadTypes.forEach((type) =>
      queryParams.append("upload_types", type),
    );
  }
  if (options?.isInBatch !== undefined) {
    queryParams.append("is_in_batch", String(options.isInBatch));
  }

  const url = `${urlPrefix}/${session.clinicId}/patients/${patientId}/images?${queryParams.toString()}`;

  try {
    const response = await fetchWithAuthRetry(url, {
      method: "GET",
    });

    if (!response.ok) {
      throw new Error(`HTTP error ${response.status}`);
    }

    const data = await response.json();
    return data.images as TGetPatientImageData[];
  } catch (error) {
    console.error("Error fetching patient images:", error);
    throw error;
  }
};

export const patchUpdatePlan = async (session: TSession, plan: TPlan) => {
  const body = JSON.stringify({ plan: plan });

  try {
    const response = await fetchWithAuthRetry(
      `${protocol}://${host}/clinics/v1/billing/${session.clinicId}/update-subscription`,
      {
        method: "PATCH",
        body,
      },
    );

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error updating plan:", error);
    throw error;
  }
};

export type TPlanGetResponse = {
  clinic_uuid: string;
  stripe_customer_id: string;
  plan_type: TPlan;
  is_payment_delinquent: boolean;
  remaining_free_simulation_count: number;
  free_simulations_used_count: number;
};

export type TPlanPricingItem = {
  plan: TPlan;
  price_id: string;
  unit_amount: number | null;
  currency: string;
  interval: string | null;
  interval_count: number | null;
};

export type TCommunicationsPlanPricingItem = {
  plan_code: string;
  monthly_price_cents: number;
  currency: string;
  included_number_count: number;
  included_minutes: number;
  extra_number_monthly_price_cents: number;
  user_seat_monthly_price_cents: number;
  overage_minute_price_cents: number;
  billing_interval: string | null;
  stripe_base_price_id: string | null;
  stripe_user_seat_price_id: string | null;
  stripe_extra_number_price_id: string | null;
  stripe_usage_price_id: string | null;
};

export type TPlanPricingGetResponse = {
  prices: TPlanPricingItem[];
  communications_pricing: TCommunicationsPlanPricingItem | null;
};

export const getClinicPlan = async (
  session: Pick<TSession, "accessToken" | "clinicId">,
  options: { signal?: AbortSignal } = {},
): Promise<TPlanGetResponse> => {
  const response = await fetchWithAuthRetry(
    `${protocol}://${host}/clinics/v1/billing/${session.clinicId}/payments`,
    {
      method: "GET",
      signal: options.signal,
    },
  );

  if (!response.ok) {
    throw new Error(`HTTP error! Status: ${response.status}`);
  }

  const data: TPlanGetResponse = await response.json();
  return data;
};

export const getPlanPricing = async (
  session: Pick<TSession, "accessToken" | "clinicId">,
  options: { signal?: AbortSignal } = {},
): Promise<TPlanPricingGetResponse> => {
  const response = await fetchWithAuthRetry(
    `${protocol}://${host}/clinics/v1/billing/${session.clinicId}/plan-pricing`,
    {
      method: "GET",
      signal: options.signal,
    },
  );

  if (!response.ok) {
    throw new Error(`HTTP error! Status: ${response.status}`);
  }

  const data: TPlanPricingGetResponse = await response.json();
  return data;
};

export const createBillingPortalSession = async (session: TSession) => {
  const response = await fetchWithAuthRetry(
    `${protocol}://${host}/clinics/v1/billing/${session.clinicId}/billing-portal`,
    {
      method: "POST",
    },
  );

  if (!response.ok) {
    const text = await response.text();
    throw new Error(`HTTP error ${response.status}: ${text}`);
  }

  return await response.json();
};

export type TGetClinicSdkKeysResponse = {
  sdk_keys: string[];
};

export const getClinicSdkKeys = async (
  sessionAccessToken: string,
  clinicId: string,
  options: { signal?: AbortSignal } = {},
) => {
  try {
    const response = await fetchWithAuthRetry(
      `${protocol}://${host}/clinics/v1/mgmt-clinic/clinics/${clinicId}/sdk-keys`,
      {
        method: "GET",
        signal: options.signal,
      },
    );
    if (!response.ok) {
      throw new Error(`HTTP error ${response.status}`);
    }
    const data = await response.json();
    const results = data.results || [];
    if (results.length === 0) {
      throw new Error("No sdk keys found");
    }

    return { results };
  } catch (error) {
    console.error("Error getting clinic sdk keys:", error);
    throw error;
  }
};

export type TPatchShowTipsResquest = {
  show_onboard_tips_before_after: boolean;
};

export const patchShowTips = async (
  session: TSession,
  body: TPatchShowTipsResquest,
) => {
  try {
    const response = await fetchWithAuthRetry(
      `${urlPrefixUser}/${session.userId}/clinics/${session.clinicId}/show-tips`,
      {
        method: "PATCH",
        body: JSON.stringify(body),
      },
    );

    if (!response.ok) {
      throw new Error(`HTTP error ${response.status}`);
    }
  } catch (error) {
    console.error("Error getting clinic sdk keys:", error);
    throw error;
  }
};

export type TClinicEmailVerificationSignalRequest = {
  clinic_email: string;
  clinic_id?: string | null;
  clinic_name?: string | null;
};

export type TClinicEmailVerificationSignalResponse = {
  accepted: boolean;
  lead_id: number;
  trial_extended: boolean;
  free_trial_end_date: string | null;
};

export const postClinicEmailVerificationSignal = async (
  body: TClinicEmailVerificationSignalRequest,
): Promise<TClinicEmailVerificationSignalResponse> => {
  const response = await fetchWithAuthRetry(
    `${urlPrefixUser}/clinic-email-verification-signal`,
    {
      method: "POST",
      body: JSON.stringify(body),
    },
  );

  if (!response.ok) {
    const data = await response
      .clone()
      .json()
      .catch(() => null);
    const detail =
      data?.detail || data?.error || `HTTP error ${response.status}`;
    throw new Error(String(detail));
  }

  return response.json();
};

export type TPostUsersResquest = {
  invite_code: string;
  supabase_uuid: string;
  is_patient_user: boolean;
};

export type TPostUsersResponse = {
  role: string;
  clinic_uuid: string;
  patient_uuid: string;
  supabase_uuid: string;
  completed_setup: boolean;
  show_onboard_tips_before_after: boolean;
};
export const postUsers = async (
  session: TSession,
  body: TPostUsersResquest,
): Promise<TPostUsersResponse> => {
  try {
    const response = await fetchWithAuthRetry(`${urlPrefixUser}/users`, {
      method: "POST",
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new Error(`HTTP error ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error("Error getting clinic sdk keys:", error);
    throw error;
  }
};

export type TPostClinicUserInviteRequest = {
  email: string;
  role?: "CLINIC_STAFF" | "CLINIC_ADMIN";
  redirect_to: string;
};

export type TPostClinicUserInviteResponse = {
  invited_user_uuid: string | null;
  email: string;
  role: "CLINIC_STAFF" | "CLINIC_ADMIN";
  clinic_id: string;
  invite_status: string;
};

export const postClinicUserInvite = async (
  session: TSession,
  body: TPostClinicUserInviteRequest,
): Promise<TPostClinicUserInviteResponse> => {
  if (!session.clinicId) {
    throw new Error("Session is missing clinic information.");
  }

  const response = await fetchWithAuthRetry(
    `${urlPrefixMgmtClinic}/${session.clinicId}/users/invite`,
    {
      method: "POST",
      body: JSON.stringify(body),
    },
  );

  const errorBody = await response
    .clone()
    .json()
    .catch(() => null);

  if (response.ok) {
    return await response.json();
  }

  if (response.status === 400) {
    throw new Error(errorBody?.message ?? "Please enter a valid email.");
  }
  if (response.status === 403) {
    throw new Error("Only clinic admins can send staff invites.");
  }
  if (response.status === 409) {
    if (errorBody?.error_code === "CLINICUSER__INVITE_ALREADY_MEMBER") {
      throw new Error("This user is already a member of your clinic.");
    }
    throw new Error(errorBody?.message ?? "This user is already invited.");
  }
  if (response.status === 429) {
    throw new Error("Invite rate limit reached. Please try again later.");
  }
  if (response.status === 502) {
    throw new Error(
      "Invite provider is temporarily unavailable. Please try again.",
    );
  }

  throw new Error(
    errorBody?.message ??
      `Failed to send staff invite (HTTP ${response.status}).`,
  );
};

export type TPostClaimMyClinicInviteResponse = {
  clinic_id: string;
  user_uuid: string;
  claimed: boolean;
  already_member: boolean;
  claimed_invite_id: number | null;
  role: "CLINIC_STAFF" | "CLINIC_ADMIN";
};

export const postClaimMyClinicInvite = async (
  clinicId: string,
): Promise<TPostClaimMyClinicInviteResponse> => {
  const response = await fetchWithAuthRetry(
    `${urlPrefixUser}/clinics/${clinicId}/invite/claim-me`,
    {
      method: "POST",
    },
  );
  const errorBody = await response
    .clone()
    .json()
    .catch(() => null);

  if (response.ok) {
    return await response.json();
  }
  if (response.status === 404) {
    throw new Error(
      "No active invite was found for this clinic. Ask your clinic admin to send a new invite.",
    );
  }
  if (response.status === 403) {
    throw new Error(
      "Your invite session is invalid. Please use a new invite link.",
    );
  }
  if (response.status === 409) {
    throw new Error(
      errorBody?.message ??
        "Your current account cannot claim this invite due to a role conflict.",
    );
  }

  throw new Error(
    errorBody?.message ??
      `Failed to claim clinic invite (HTTP ${response.status}).`,
  );
};

export type TPatchPatientsInviteQuery = {
  patient_uuid: string;
};
export type TPatchPatientsInviteResponse = {
  uuid: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  dob: string;
  sex: string;
  areas_of_interest: string[];
  procedures_of_interest: string[];
  created_at: string;
  updated_at: string;
  upload_type: string;
  location: string;
  invite_code: string;
  invite_code_expires_at: string;
};

export const patchPatientsInvite = async (
  session: TSession,
  query: TPatchPatientsInviteQuery,
) => {
  try {
    const response = await fetchWithAuthRetry(
      `${urlPrefixUser}/patients/${query.patient_uuid}/invite`,
      {
        method: "PATCH",
      },
    );

    if (!response.ok) {
      throw new Error(`HTTP error ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error("Error getting clinic sdk keys:", error);
    throw error;
  }
};

export type TClinicUsageResponse = {
  clinic_uuid: string;
  total_patients: number;
  patients_by_type: Record<string, number>;
  total_form_submissions: number;
  total_side_by_side_images: number;
  total_audio_files: number;
  total_simulations: number;
  total_conversations: number;
  total_messages: number;
};
export const getClinicUsage = async (
  session: TSession,
): Promise<TClinicUsageResponse> => {
  const response = await fetchWithAuthRetry(
    `${protocol}://${host}/clinics/v1/clinic/${session.clinicId}/usage`,
    {
      method: "GET",
    },
  );

  if (!response.ok) {
    throw new Error(`HTTP error! Status: ${response.status}`);
  }

  const data: TClinicUsageResponse = await response.json();
  return data;
};
